import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { AlertCircle, ArrowRightLeft, ChevronRight, CornerDownRight, FileText, Hash, Link2, MoreHorizontal, Pencil, Plus, Trash2 } from "lucide-react";
import { keys, useDeleteNote, useNote, usePatchNote, useSpace } from "@/api/hooks";
import { errorMessage, isApiError } from "@/api/client";
import type { Note, NoteLink } from "@/api/types";
import type { Block, ChecklistItem } from "@/lib/blocks";
import { formatDateTime, timeAgo } from "@/lib/utils";
import { useToast } from "@/lib/toast";
import { BlockRenderer } from "@/components/blocks/BlockRenderer";
import { OutlineRail, columnClass, frameClass, hasRail } from "@/components/OutlineRail";
import { ClientBadge } from "@/components/ClientBadge";
import { ErrorState, EmptyState } from "@/components/States";
import { MoveNoteDialog } from "@/components/MoveNoteDialog";
import { ShareMenu } from "@/components/ShareDialog";
import { Button } from "@/components/ui/button";
import { PageSkeleton } from "@/components/ui/skeleton";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

export default function NotePage() {
  const { id = "" } = useParams<{ id: string }>();
  const note = useNote(id);
  if (note.isLoading) return <PageSkeleton />;
  if (note.isError) {
    if (isApiError(note.error, 404)) return <EmptyState icon={FileText} title="Note not found" description="It may have been deleted." action={<Button asChild variant="outline" size="sm"><Link to="/">Back to recent</Link></Button>} />;
    return <ErrorState error={note.error} retry={() => note.refetch()} />;
  }
  // Keyed so per-note state (pending toggles, dialogs, memoised handlers) resets on navigation.
  return <NoteView key={note.data!.id} note={note.data!} />;
}

function NoteView({ note }: { note: Note }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const space = useSpace(note.spaceId);
  const patch = usePatchNote(note.id);
  const del = useDeleteNote();
  const [pending, setPending] = useState<Set<string>>(() => new Set());
  const [move, setMove] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Scroll to #block-id once the content is there, and highlight it briefly.
  useEffect(() => {
    const hash = location.hash.replace(/^#/, "");
    if (!hash) return;
    const el = document.getElementById(hash);
    if (!el) return;
    el.scrollIntoView({ block: "start", behavior: "smooth" });
    el.classList.add("ring-2", "ring-ring/50", "rounded-xl");
    const t = setTimeout(() => el.classList.remove("ring-2", "ring-ring/50", "rounded-xl"), 1800);
    return () => clearTimeout(t);
  }, [location.hash, note.id, note.version]);

  const onToggleCheck = (blockId: string, index: number, checked: boolean) => {
    const current = qc.getQueryData<Note>(keys.note(note.id));
    if (!current) return;
    qc.setQueryData(keys.note(note.id), { ...current, blocks: toggleInBlocks(current.blocks, blockId, index, checked) });
    setPending((s) => new Set(s).add(blockId));
    patch.mutate(
      { version: current.version, ops: [{ op: "toggle_check", block_id: blockId, index, checked }] },
      {
        onError: (err) => {
          qc.setQueryData(keys.note(note.id), current);
          if (isApiError(err, 409)) {
            toast({
              title: "This note changed elsewhere",
              description: "Reload it to see the latest version, then tick again.",
              tone: "error",
              action: { label: "Reload note", onClick: () => qc.invalidateQueries({ queryKey: keys.note(note.id) }) },
            });
          } else {
            toast({ title: "Could not update checklist", description: errorMessage(err), tone: "error" });
          }
        },
        onSettled: () =>
          setPending((s) => {
            const n = new Set(s);
            n.delete(blockId);
            return n;
          }),
      },
    );
  };

  const actions = useMemo(() => ({ onToggleCheck, pending }), [pending]); // eslint-disable-line react-hooks/exhaustive-deps

  const outline = note.outline ?? [];
  const showRail = hasRail(outline);
  const children = note.children ?? [];
  // The page header already shows the title; don't render the leading H1 block twice.
  const first = note.blocks[0];
  const bodyBlocks = first && first.type === "heading" && first.depth === 1 ? note.blocks.slice(1) : note.blocks;
  const backlinks = note.backlinks ?? [];
  const links = note.links ?? [];

  return (
    <div className={frameClass(showRail)}>
      <article className={columnClass(showRail)}>
        {/* Breadcrumbs */}
        <nav aria-label="Breadcrumb" className="mb-3 flex flex-wrap items-center gap-1 text-sm text-muted-foreground">
          <Link to={`/spaces/${note.spaceId}`} className="hover:text-foreground">
            {space.data?.name ?? "Space"}
          </Link>
          {(note.breadcrumbs ?? []).map((b) => (
            <span key={b.id} className="flex items-center gap-1">
              <ChevronRight className="size-3.5" />
              <Link to={`/notes/${b.id}`} className="max-w-48 truncate hover:text-foreground">
                {b.title || "Untitled"}
              </Link>
            </span>
          ))}
        </nav>

        {/* Header */}
        <header className="mb-6">
          <div className="flex items-start justify-between gap-4">
            <h1 className="text-3xl font-semibold leading-tight tracking-tight">{note.title || "Untitled"}</h1>
            <div className="flex shrink-0 items-center gap-1.5">
              <Button asChild variant="outline" size="sm" className="hidden sm:inline-flex">
                <Link to={`/notes/${note.id}/edit`}>
                  <Pencil /> Edit markdown
                </Link>
              </Button>
              <ShareMenu noteId={note.id} />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon-sm" aria-label="More">
                    <MoreHorizontal />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => navigate(`/notes/${note.id}/edit`)} className="sm:hidden">
                    <Pencil /> Edit markdown
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => navigate(`/spaces/${note.spaceId}/new?parent=${note.id}`)}>
                    <Plus /> New page inside
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setMove(true)}>
                    <ArrowRightLeft /> Move…
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => navigator.clipboard?.writeText(note.id).then(() => toast({ title: "Note id copied" }))}>
                    <Hash /> Copy note id
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem destructive onSelect={() => setConfirmDelete(true)}>
                    <Trash2 /> Delete note
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <ClientBadge client={note.updatedByClient} />
            <Tooltip>
              <TooltipTrigger asChild>
                <span>updated {timeAgo(note.updatedAt)}</span>
              </TooltipTrigger>
              <TooltipContent>{formatDateTime(note.updatedAt)}</TooltipContent>
            </Tooltip>
            <span>v{note.version}</span>
            <span className="font-mono">{note.id.length > 12 ? note.id.slice(0, 8) + "…" : note.id}</span>
          </div>
        </header>

        {note.errors && note.errors.length > 0 && (
          <div className="mb-5 flex gap-2 rounded-lg border border-tone-warning/40 bg-tone-warning/10 px-3.5 py-2.5 text-sm">
            <AlertCircle className="mt-0.5 size-4 shrink-0 text-tone-warning" />
            <div>
              {note.errors.length} {note.errors.length === 1 ? "block has" : "blocks have"} invalid components; shown as raw code below.
            </div>
          </div>
        )}

        {/* Content */}
        {note.blocks.length === 0 ? (
          <EmptyState icon={FileText} title="This note is empty" action={<Button asChild size="sm" variant="outline"><Link to={`/notes/${note.id}/edit`}>Write something</Link></Button>} />
        ) : (
          <BlockRenderer blocks={bodyBlocks} actions={actions} />
        )}

        {/* Pages inside */}
        {children.length > 0 && (
          <section className="mt-10">
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Pages inside</h2>
            <ul className="divide-y rounded-xl border bg-card">
              {children.map((c) => (
                <li key={c.id}>
                  <Link to={`/notes/${c.id}`} className="flex items-center gap-2.5 px-3.5 py-2.5 text-sm hover:bg-muted/50">
                    <CornerDownRight className="size-4 text-muted-foreground" />
                    <span className="flex-1 truncate font-medium">{c.title || "Untitled"}</span>
                    {c.updatedAt && <span className="text-xs text-muted-foreground">{timeAgo(c.updatedAt)}</span>}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Links */}
        {(backlinks.length > 0 || links.length > 0) && (
          <section className="mt-10 grid gap-6 sm:grid-cols-2">
            <LinkList title="Linked from" links={backlinks} empty="No backlinks" />
            <LinkList title="Links to" links={links} empty="No outgoing links" />
          </section>
        )}

        <footer className="mt-12 flex flex-wrap items-center justify-between gap-3 border-t pt-4 text-xs text-muted-foreground">
          <span>
            Hover a block to see its id; models patch blocks by id. Created {note.createdAt ? formatDateTime(note.createdAt) : "—"}.
          </span>
          <Button asChild variant="ghost" size="xs">
            <Link to={`/notes/${note.id}/edit`}>
              <Pencil /> Edit markdown
            </Link>
          </Button>
        </footer>
      </article>

      {showRail && <OutlineRail outline={outline} activeHash={location.hash} />}

      <MoveNoteDialog note={note} open={move} onOpenChange={setMove} />
      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete “{note.title || "Untitled"}”?</DialogTitle>
            <DialogDescription>
              This cannot be undone. {children.length > 0 && `${children.length} nested ${children.length === 1 ? "page moves" : "pages move"} up to the parent.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={del.isPending}
              onClick={() =>
                del.mutate(
                  { id: note.id, spaceId: note.spaceId, parentId: note.parentId },
                  {
                    onSuccess: () => {
                      toast({ title: "Note deleted" });
                      navigate(note.parentId ? `/notes/${note.parentId}` : `/spaces/${note.spaceId}`, { replace: true });
                    },
                    onError: (err) => toast({ title: "Could not delete", description: errorMessage(err), tone: "error" }),
                  },
                )
              }
            >
              {del.isPending ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function linkTarget(l: NoteLink): { id: string | null; title: string } {
  const id = l.noteId ?? l.id ?? null;
  return { id, title: l.title ?? l.targetTitle ?? (id ? id : "unresolved") };
}

function LinkList({ title, links, empty }: { title: string; links: NoteLink[]; empty: string }) {
  return (
    <div>
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</h2>
      {links.length === 0 ? (
        <div className="text-sm text-muted-foreground">{empty}</div>
      ) : (
        <ul className="flex flex-col gap-1 text-sm">
          {links.map((l, i) => {
            const t = linkTarget(l);
            return (
              <li key={`${t.id ?? t.title}-${i}`} className="flex items-center gap-2">
                <Link2 className="size-3.5 shrink-0 text-muted-foreground" />
                {t.id ? (
                  <Link to={`/notes/${t.id}`} className="truncate text-link hover:underline">
                    {t.title}
                  </Link>
                ) : (
                  <span className="truncate text-muted-foreground" title="No note with this title yet">
                    [[{t.title}]]
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function toggleInBlocks(blocks: Block[], blockId: string, index: number, checked: boolean): Block[] {
  return blocks.map((b) => {
    if (b.type !== "component") return b;
    if (b.id === blockId && b.props && Array.isArray(b.props.items)) {
      const items = (b.props.items as ChecklistItem[]).map((it, i) => (i === index ? { ...it, checked } : it));
      return { ...b, props: { ...b.props, items } };
    }
    if (b.children) return { ...b, children: toggleInBlocks(b.children, blockId, index, checked) };
    return b;
  });
}
